import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, canDelete, can } from '../lib/auth';

interface DieRow {
  id: string;
  dieNo: string;
  designNo: string;
  dieUps: string;
  dieVendor: string;
  createdOn: string | null;
}

interface ListResp { rows: DieRow[]; total: number; page: number; pageSize: number }

const PAGE_SIZE = 50;

export function DieDesignsPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['die-designs', { page, q }],
    queryFn: () =>
      api<ListResp>(
        `/api/die-designs?page=${page}&pageSize=${PAGE_SIZE}` + (q ? `&q=${encodeURIComponent(q)}` : ''),
      ),
  });

  const { user } = useAuth();
  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api('/api/die-designs/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['die-designs'] }); },
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set('q', search); else p.delete('q');
    p.set('page', '1');
    setParams(p);
  }
  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Die Designs</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active</p>
        </div>
        {can(user, 'EDIT_MASTERS') && (
          <Link to="/die-designs/new" className="btn-primary">+ New Die Design</Link>
        )}
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input className="input" placeholder="Die No or Design No…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {q && <button type="button" className="btn-ghost" onClick={() => { setSearch(''); setParams({}); }}>Reset</button>}
        {canDelete(user) && selected.size > 0 && (
          <button type="button" className="btn-danger" disabled={del.isPending}
            onClick={() => { if (confirm(`Delete ${selected.size}?`)) del.mutate(Array.from(selected)); }}>
            Delete {selected.size}
          </button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} /></th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Die No</th>
                <th className="px-3 py-2">Design No</th>
                <th className="px-3 py-2">Ups</th>
                <th className="px-3 py-2">Vendor</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {list.isError && <tr><td colSpan={7} className="p-8 text-center text-red-600">Failed to load: {(list.error as Error).message}</td></tr>}
              {!list.isLoading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No records found.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2 font-medium">{r.dieNo}</td>
                  <td className="px-3 py-2">{r.designNo}</td>
                  <td className="px-3 py-2 text-slate-600">{r.dieUps}</td>
                  <td className="px-3 py-2 text-slate-700">{r.dieVendor}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/die-designs/${encodeURIComponent(r.id)}/edit`} className="text-brand hover:underline text-sm">Edit</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹ Prev</button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next ›</button>
        </div>
      </div>
    </div>
  );
}
